import axios from "axios";
import { reactive } from "vue";
import {
  createSmartTrackerInitialState,
  hasSmartTrackerAuthToken,
  normalizeSmartTrackerPositiveInt,
  normalizeSmartTrackerSessionState,
  resolveSmartTrackerModuleKey,
} from "@/Utile/smartTrackerRuntimeUtils.mjs";

const DEFAULT_IDLE_SECONDS = 180;
const DEFAULT_PROMPT_SECONDS = 30;
const HEARTBEAT_INTERVAL_MS = 20_000;
const IDLE_TICK_INTERVAL_MS = 1_000;
const DISABLED_RETRY_MS = 60_000;
const ERROR_RETRY_MS = 15_000;
const LOCK_RETRY_MS = 2_500;
const RESUME_COOLDOWN_MS = 2_000;
const HEARTBEAT_MIN_GAP_MS = 7_000;
const MULTI_TAB_ACTIVITY_STORAGE_PREFIX = "smart-tracker:last-activity";
const MULTI_TAB_CHANNEL_PREFIX = "smart-tracker:sync";
const MULTI_TAB_ACTIVITY_SYNC_MIN_GAP_MS = 750;

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "focus"];

const TRACKING_DISABLED_PATHS = new Set(["/login", "/register", "/verify-email", "/forgot-password", "/reset-password", "/link-google", "/no-company-context"]);

const singleton = {
  runtime: null,
};

function shouldTrackPath(routePath) {
  const normalized = String(routePath || "").trim().toLowerCase() || "/";
  if (TRACKING_DISABLED_PATHS.has(normalized)) return false;
  return !normalized.startsWith("/login");
}

const hasAuthToken = () => hasSmartTrackerAuthToken();

function createRuntime() {
  const state = reactive(createSmartTrackerInitialState({
    idleSeconds: DEFAULT_IDLE_SECONDS,
    promptSeconds: DEFAULT_PROMPT_SECONDS,
  }));
  const tabId = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  let heartbeatTimer = null;
  let idleTickTimer = null;
  let bootstrapRetryTimer = null;
  let bootstrapInFlight = false;
  let bootstrapTerminalFailure = false;
  let transitionInFlight = false;
  let nextBootstrapAt = 0;
  let lastResumeAttemptAt = 0;
  let syncChannel = null;
  let syncChannelName = "";
  let syncActivityStorageKey = "";
  let lastSharedActivityAt = 0;
  let lastSharedActivitySyncAt = 0;

  const parseActivityTimestamp = (value) => {
    const parsed = Number.parseInt(String(value ?? ""), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };

  const resolveMultiTabScope = () => {
    const companyId = String(localStorage.getItem("user-company-id") || "").trim();
    const employeeId = String(localStorage.getItem("user-employee-id") || "").trim();
    if (companyId && employeeId) return `${companyId}:${employeeId}`;
    if (employeeId) return `employee:${employeeId}`;
    return "global";
  };

  const closeSyncChannel = () => {
    if (!syncChannel) return;
    try {
      syncChannel.onmessage = null;
      syncChannel.close();
    } catch (_) {
      // noop
    } finally {
      syncChannel = null;
    }
  };

  const applySharedActivityTimestamp = (timestamp) => {
    const parsed = parseActivityTimestamp(timestamp);
    if (!parsed) return;
    if (parsed > lastSharedActivityAt) {
      lastSharedActivityAt = parsed;
    }
    if (parsed > state.lastActivityAt) {
      state.lastActivityAt = parsed;
    }
    if (state.promptVisible) {
      hidePrompt();
    }
  };

  const readSharedActivityFromStorage = () => {
    if (!syncActivityStorageKey) return 0;
    try {
      const raw = localStorage.getItem(syncActivityStorageKey);
      const parsed = parseActivityTimestamp(raw);
      if (parsed > lastSharedActivityAt) {
        lastSharedActivityAt = parsed;
      }
      return parsed;
    } catch (_) {
      return lastSharedActivityAt;
    }
  };

  const getGlobalLastActivityAt = () => {
    const fromStorage = readSharedActivityFromStorage();
    return Math.max(
      parseActivityTimestamp(state.lastActivityAt),
      parseActivityTimestamp(lastSharedActivityAt),
      parseActivityTimestamp(fromStorage)
    );
  };

  const syncActivityAcrossTabs = (timestamp, { force = false } = {}) => {
    const activityAt = parseActivityTimestamp(timestamp);
    if (!activityAt || !hasAuthToken()) return;
    if (!syncActivityStorageKey) return;
    if (!force && activityAt - lastSharedActivitySyncAt < MULTI_TAB_ACTIVITY_SYNC_MIN_GAP_MS) {
      return;
    }
    lastSharedActivitySyncAt = activityAt;
    applySharedActivityTimestamp(activityAt);

    try {
      localStorage.setItem(syncActivityStorageKey, String(activityAt));
    } catch (_) {
      // noop
    }

    if (syncChannel) {
      try {
        syncChannel.postMessage({
          type: "activity",
          tab_id: tabId,
          activity_at: activityAt,
        });
      } catch (_) {
        // noop
      }
    }
  };

  const handleStorageActivity = (event) => {
    if (!syncActivityStorageKey) return;
    if (!event || event.key !== syncActivityStorageKey) return;
    const parsed = parseActivityTimestamp(event.newValue);
    if (!parsed) return;
    applySharedActivityTimestamp(parsed);
  };

  const handleBroadcastMessage = (event) => {
    const payload = event?.data;
    if (!payload || payload.type !== "activity") return;
    if (payload.tab_id && payload.tab_id === tabId) return;
    const parsed = parseActivityTimestamp(payload.activity_at);
    if (!parsed) return;
    applySharedActivityTimestamp(parsed);
  };

  const refreshMultiTabSync = () => {
    const scope = resolveMultiTabScope();
    const nextStorageKey = `${MULTI_TAB_ACTIVITY_STORAGE_PREFIX}:${scope}`;
    const nextChannelName = `${MULTI_TAB_CHANNEL_PREFIX}:${scope}`;
    if (syncActivityStorageKey !== nextStorageKey) {
      syncActivityStorageKey = nextStorageKey;
      readSharedActivityFromStorage();
    }
    if (syncChannelName !== nextChannelName) {
      closeSyncChannel();
      syncChannelName = nextChannelName;
      if (typeof window !== "undefined" && typeof window.BroadcastChannel === "function") {
        try {
          syncChannel = new window.BroadcastChannel(syncChannelName);
          syncChannel.onmessage = handleBroadcastMessage;
        } catch (_) {
          syncChannel = null;
        }
      }
    }
  };

  const resetMultiTabSync = () => {
    closeSyncChannel();
    syncChannelName = "";
    syncActivityStorageKey = "";
    lastSharedActivityAt = 0;
    lastSharedActivitySyncAt = 0;
  };

  const buildContextPayload = () => {
    const routePath = state.routePath || window.location.pathname || "/";
    const moduleKey = resolveSmartTrackerModuleKey(routePath);
    state.moduleKey = moduleKey;
    return {
      module_key: moduleKey,
      route_path: routePath,
      context_key: moduleKey,
      context_label: moduleKey,
    };
  };

  const applySettings = (settings = {}) => {
    state.idleSeconds = normalizeSmartTrackerPositiveInt(
      settings?.smart_tracker_idle_seconds,
      DEFAULT_IDLE_SECONDS,
      30,
      7200
    );
    state.promptSeconds = normalizeSmartTrackerPositiveInt(
      settings?.smart_tracker_prompt_seconds,
      DEFAULT_PROMPT_SECONDS,
      10,
      3600
    );
    state.enabled = Boolean(settings?.smart_tracker_enabled);
  };

  const applySession = (session = null) => {
    if (!session || typeof session !== "object") {
      state.sessionId = null;
      state.sessionState = "stopped";
      return;
    }
    state.sessionId = Number(session.id || 0) || null;
    state.sessionState = normalizeSmartTrackerSessionState(session.state, "active");
  };

  const hidePrompt = () => {
    state.promptVisible = false;
    state.promptCountdown = state.promptSeconds;
    state.promptDeadlineAt = 0;
  };

  const showPrompt = () => {
    state.promptVisible = true;
    state.promptDeadlineAt = Date.now() + state.promptSeconds * 1000;
    state.promptCountdown = state.promptSeconds;
  };

  const clearBootstrapRetry = () => {
    if (bootstrapRetryTimer) {
      clearTimeout(bootstrapRetryTimer);
      bootstrapRetryTimer = null;
    }
  };

  const scheduleBootstrapRetry = (delayMs = ERROR_RETRY_MS) => {
    if (!state.initialized) return;
    if (bootstrapRetryTimer) return;
    bootstrapRetryTimer = setTimeout(() => {
      bootstrapRetryTimer = null;
      void bootstrap({ force: true });
    }, delayMs);
  };

  const clearTimers = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    if (idleTickTimer) {
      clearInterval(idleTickTimer);
      idleTickTimer = null;
    }
    clearBootstrapRetry();
  };

  const ensureTimers = () => {
    if (heartbeatTimer === null) {
      heartbeatTimer = setInterval(() => {
        void sendHeartbeat({ force: false });
      }, HEARTBEAT_INTERVAL_MS);
    }
    if (idleTickTimer === null) {
      idleTickTimer = setInterval(() => {
        runIdleTick();
      }, IDLE_TICK_INTERVAL_MS);
    }
  };

  const pauseSession = async ({ stopReason = "idle_timeout" } = {}) => {
    if (!state.enabled || state.sessionState !== "active" || !state.sessionId) {
      return;
    }
    const pauseThresholdMs = (state.idleSeconds + state.promptSeconds) * 1000;
    const now = Date.now();
    const totalIdleMs = Math.max(0, now - getGlobalLastActivityAt());
    if (totalIdleMs < pauseThresholdMs) {
      return;
    }
    if (transitionInFlight) return;
    transitionInFlight = true;
    try {
      const payload = {
        session_id: state.sessionId,
        occurred_at: new Date().toISOString(),
        stop_reason: stopReason,
        ...buildContextPayload(),
      };
      const { data } = await axios.post("/smart-tracker/session/pause", payload);
      applySession(data?.session || null);
      state.lastHeartbeatAt = Date.now();
    } catch (error) {
      const code = Number(error?.response?.status || 0);
      if (code === 404) {
        state.sessionId = null;
        state.sessionState = "stopped";
      }
    } finally {
      transitionInFlight = false;
    }
  };

  const resumeSession = async () => {
    if (!state.enabled || state.sessionState !== "paused" || !state.sessionId) {
      return;
    }
    const now = Date.now();
    if (now - lastResumeAttemptAt < RESUME_COOLDOWN_MS) {
      return;
    }
    if (transitionInFlight) return;

    transitionInFlight = true;
    lastResumeAttemptAt = now;
    try {
      const payload = {
        session_id: state.sessionId,
        occurred_at: new Date().toISOString(),
        ...buildContextPayload(),
      };
      const { data } = await axios.post("/smart-tracker/session/resume", payload);
      applySession(data?.session || null);
      state.lastHeartbeatAt = Date.now();
    } catch (error) {
      const code = Number(error?.response?.status || 0);
      if (code === 404) {
        state.sessionId = null;
        state.sessionState = "stopped";
      }
    } finally {
      transitionInFlight = false;
    }
  };

  const sendHeartbeat = async ({ force = false } = {}) => {
    if (!state.enabled || state.sessionState !== "active" || !state.sessionId) return;
    if (!hasAuthToken()) return;
    if (!shouldTrackPath(state.routePath)) return;

    const now = Date.now();
    if (!force && now - state.lastHeartbeatAt < HEARTBEAT_MIN_GAP_MS) {
      return;
    }

    try {
      const payload = {
        session_id: state.sessionId,
        occurred_at: new Date().toISOString(),
        ...buildContextPayload(),
      };
      const { data } = await axios.post("/smart-tracker/session/heartbeat", payload);
      applySession(data?.session || null);
      state.lastHeartbeatAt = now;
    } catch (error) {
      const code = Number(error?.response?.status || 0);
      if (code === 404 || code === 409) {
        state.sessionId = null;
        state.sessionState = "stopped";
        void bootstrap({ force: false });
      }
    }
  };

  const bootstrap = async ({ force = false } = {}) => {
    if (!hasAuthToken()) {
      bootstrapTerminalFailure = false;
      resetMultiTabSync();
      state.ready = true;
      state.enabled = false;
      hidePrompt();
      clearTimers();
      return;
    }
    if (bootstrapTerminalFailure) {
      return;
    }
    if (!shouldTrackPath(state.routePath)) {
      hidePrompt();
      clearTimers();
      return;
    }
    refreshMultiTabSync();

    const now = Date.now();
    if (!force && now < nextBootstrapAt) {
      return;
    }
    if (bootstrapInFlight) return;

    bootstrapInFlight = true;
    try {
      const payload = {
        occurred_at: new Date().toISOString(),
        ...buildContextPayload(),
      };
      const { data } = await axios.post("/smart-tracker/session/start", payload);
      applySession(data?.session || null);
      applySettings(data?.settings || {});
      state.ready = true;
      state.errorMessage = "";
      state.lastActivityAt = Date.now();
      state.lastHeartbeatAt = Date.now();
      bootstrapTerminalFailure = false;
      nextBootstrapAt = 0;
      clearBootstrapRetry();

      if (state.enabled) {
        ensureTimers();
      } else {
        hidePrompt();
        clearTimers();
      }
    } catch (error) {
      const code = Number(error?.response?.status || 0);
      if (code === 409) {
        bootstrapTerminalFailure = false;
        applySettings(error?.response?.data?.settings || {});
        state.ready = true;
        state.sessionId = null;
        state.sessionState = "stopped";
        state.errorMessage = "";
        hidePrompt();
        clearTimers();
        nextBootstrapAt = Date.now() + DISABLED_RETRY_MS;
        scheduleBootstrapRetry(DISABLED_RETRY_MS);
      } else if (code === 423) {
        bootstrapTerminalFailure = false;
        state.errorMessage = String(error?.response?.data?.error || "Smart Tracker este ocupat temporar.");
        nextBootstrapAt = Date.now() + LOCK_RETRY_MS;
        scheduleBootstrapRetry(LOCK_RETRY_MS);
      } else if (code === 0 || code === 429 || code >= 500) {
        bootstrapTerminalFailure = false;
        state.errorMessage = String(error?.response?.data?.error || "Smart Tracker bootstrap failed.");
        nextBootstrapAt = Date.now() + ERROR_RETRY_MS;
        scheduleBootstrapRetry(ERROR_RETRY_MS);
      } else {
        bootstrapTerminalFailure = true;
        state.errorMessage = String(error?.response?.data?.error || "Smart Tracker bootstrap failed.");
        state.ready = true;
        state.enabled = false;
        state.sessionId = null;
        state.sessionState = "stopped";
        hidePrompt();
        clearTimers();
        nextBootstrapAt = Number.POSITIVE_INFINITY;
      }
    } finally {
      bootstrapInFlight = false;
    }
  };

  const runIdleTick = () => {
    if (!hasAuthToken()) {
      hidePrompt();
      clearTimers();
      return;
    }
    if (!shouldTrackPath(state.routePath)) {
      hidePrompt();
      return;
    }
    if (!state.ready || !state.sessionId || state.sessionState === "stopped" || !state.enabled) {
      void bootstrap({ force: false });
      hidePrompt();
      return;
    }
    if (state.sessionState !== "active") {
      hidePrompt();
      return;
    }

    const now = Date.now();
    const globalLastActivityAt = getGlobalLastActivityAt();
    if (globalLastActivityAt > state.lastActivityAt) {
      state.lastActivityAt = globalLastActivityAt;
    }
    const idleMs = Math.max(0, now - globalLastActivityAt);
    const idleThresholdMs = state.idleSeconds * 1000;
    const pauseThresholdMs = idleThresholdMs + state.promptSeconds * 1000;
    const isVisibleTab = typeof document === "undefined" || document.visibilityState === "visible";

    if (!state.promptVisible) {
      if (idleMs >= idleThresholdMs) {
        if (isVisibleTab) {
          showPrompt();
        } else if (idleMs >= pauseThresholdMs) {
          void pauseSession({ stopReason: "idle_timeout" });
        }
      }
      return;
    }

    if (idleMs < idleThresholdMs) {
      hidePrompt();
      return;
    }

    const remainingMs = pauseThresholdMs - idleMs;
    state.promptDeadlineAt = now + Math.max(0, remainingMs);
    state.promptCountdown = Math.max(0, Math.ceil(remainingMs / 1000));

    if (!isVisibleTab && remainingMs > 0) {
      hidePrompt();
      return;
    }

    if (remainingMs <= 0) {
      hidePrompt();
      void pauseSession({ stopReason: "idle_timeout" });
    }
  };

  const markActivity = () => {
    const activityAt = Date.now();
    state.lastActivityAt = activityAt;
    syncActivityAcrossTabs(activityAt);
    if (!hasAuthToken() || !shouldTrackPath(state.routePath)) return;
    if (!state.ready || !state.sessionId || state.sessionState === "stopped" || !state.enabled) {
      void bootstrap({ force: false });
      return;
    }

    if (state.promptVisible) {
      hidePrompt();
    }

    if (state.sessionState === "paused") {
      void resumeSession();
      return;
    }

    if (state.sessionState === "active") {
      void sendHeartbeat({ force: false });
    }
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState !== "visible") return;
    const activityAt = Date.now();
    state.lastActivityAt = activityAt;
    syncActivityAcrossTabs(activityAt, { force: true });
    void bootstrap({ force: true });
    if (state.sessionState === "paused") {
      void resumeSession();
      return;
    }
    if (state.sessionState === "active") {
      void sendHeartbeat({ force: true });
    }
  };

  const init = async (routePath = "") => {
    if (state.initialized) return;

    state.initialized = true;
    state.routePath = routePath || window.location.pathname || "/";
    state.lastActivityAt = Date.now();
    refreshMultiTabSync();
    syncActivityAcrossTabs(state.lastActivityAt, { force: true });

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markActivity, { passive: true });
    });
    window.addEventListener("storage", handleStorageActivity);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    await bootstrap({ force: true });
  };

  const destroy = () => {
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, markActivity);
    });
    window.removeEventListener("storage", handleStorageActivity);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    clearTimers();
    resetMultiTabSync();
    bootstrapTerminalFailure = false;
    nextBootstrapAt = 0;
    hidePrompt();
    Object.assign(state, createSmartTrackerInitialState({
      idleSeconds: DEFAULT_IDLE_SECONDS,
      promptSeconds: DEFAULT_PROMPT_SECONDS,
    }));
  };

  const setRoute = (routePath = "") => {
    state.routePath = routePath || window.location.pathname || "/";
    state.moduleKey = resolveSmartTrackerModuleKey(state.routePath);

    if (!shouldTrackPath(state.routePath)) {
      hidePrompt();
      clearTimers();
      return;
    }

    const activityAt = Date.now();
    state.lastActivityAt = activityAt;
    syncActivityAcrossTabs(activityAt, { force: true });
    void bootstrap({ force: false });
    if (state.enabled && state.sessionState === "active") {
      void sendHeartbeat({ force: true });
    }
  };

  const confirmPrompt = async () => {
    const activityAt = Date.now();
    state.lastActivityAt = activityAt;
    syncActivityAcrossTabs(activityAt, { force: true });
    hidePrompt();
    if (state.sessionState === "paused") {
      await resumeSession();
      return;
    }
    await sendHeartbeat({ force: true });
  };

  const pauseNow = async () => {
    hidePrompt();
    await pauseSession({ stopReason: "idle_timeout" });
  };

  const stopForLogout = async () => {
    if (!hasAuthToken()) {
      return;
    }
    try {
      await axios.post("/logout");
    } catch (logoutError) {
      if (state.enabled && state.sessionId) {
        try {
          const payload = {
            session_id: state.sessionId,
            stop_reason: "logout",
            ...buildContextPayload(),
          };
          await axios.post("/smart-tracker/session/stop", payload);
        } catch (_) {
          // best effort fallback
        }
      }
    } finally {
      hidePrompt();
      clearTimers();
      state.sessionId = null;
      state.sessionState = "stopped";
      nextBootstrapAt = 0;
    }
  };

  return {
    state,
    init,
    destroy,
    setRoute,
    confirmPrompt,
    pauseNow,
    stopForLogout,
  };
}

export function getSmartTrackerRuntime() {
  if (!singleton.runtime) {
    singleton.runtime = createRuntime();
  }
  return singleton.runtime;
}

export async function stopSmartTrackerOnLogout() {
  const runtime = getSmartTrackerRuntime();
  await runtime.stopForLogout();
}
